import {
  Box,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { t } from 'i18next';
import { FC, useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DATE_FORMAT_API } from '../../../../../../../../@constants/date';
import { ChargeCodeTypesModel } from '../../../../../../../../@constants/system';
import DateRangePicker from '../../../../../../../../components/DateRangePicker';
import { Calendar2Icon } from '../../../../../../../../components/SvgIcons/Calendar2Icon';
import { FileCsvIcon } from '../../../../../../../../components/SvgIcons/FileCsvIcon';
import { FileExcelIcon } from '../../../../../../../../components/SvgIcons/FileExcelIcon';
import { FilePdfIcon } from '../../../../../../../../components/SvgIcons/FilePdfIcon';
import { FooterExtraButtons } from '../../../../../../components/FooterButtons';
import { BillingGroupField } from '../../../../../../components/Inputs/BillingGroupField';
import { CashierField } from '../../../../../../components/Inputs/CashierField';
import { CashRegisterField } from '../../../../../../components/Inputs/CashRegisterField';
import { ChargeCodeField } from '../../../../../../components/Inputs/ChargeCodeField';
import { ChargeGroupField } from '../../../../../../components/Inputs/ChargeGroupField';
import { ReportLabel } from '../../../../../../components/ReportLabel';
import {
  TransactionListBillingNum,
  TransactionListOrderByNum,
  TransactionListTypeNum,
  TransactionsListReportFormModel,
} from '../../models';

export const FilterForm: FC<{
  onSubmit: (data: TransactionsListReportFormModel) => void;
  onReset: () => void;
}> = ({ onSubmit, onReset }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<TransactionsListReportFormModel>();

  const formValues = watch();

  const dates = useMemo(
    (): (Date | null)[] => [
      formValues?.from ? new Date(formValues?.from) : null,
      formValues?.to ? new Date(formValues?.to) : null,
    ],
    [formValues?.from, formValues?.to]
  );

  const handleChangeDates = (value: (Date | null)[]) => {
    const [from, to] = value;
    const _from = from ? format(from, DATE_FORMAT_API) : null;
    const _to = to ? format(to, DATE_FORMAT_API) : null;
    setValue('from', _from);
    setValue('to', _to);
  };

  useEffect(() => {
    setValue('charge_code_ids', []);
  }, [formValues.type]);

  return (
    <Box sx={{ p: 0, height: '100%', backgroundColor: 'colors.gray.o10', overflowY: 'scroll' }}>
      <Stack direction={'column'} spacing={0} sx={{ width: '100%', height: '100%' }}>
        <ReportLabel label="Transactions List Report" />
        <Stack direction={'row'} spacing={0}>
          <Stack
            direction={'column'}
            spacing={2}
            sx={{
              p: 3,
            }}
          >
            {/* <Controller
              control={control}
              name="date"
              render={({ field }) => (
                <DatePicker
                  name="date"
                  label="Date"
                  value={field?.value}
                  onChange={(value: string) =>
                    field.onChange(format(new Date(value), DATE_FORMAT_API))
                  }
                  error={Boolean(errors?.date)}
                  helperText={errors?.date?.message}
                  renderInput={(params: any) => <TextField variant="standard" {...params} />}
                />
              )}
            /> */}

            <DateRangePicker
              value={dates}
              onChange={handleChangeDates}
              renderInput={({ startProps, endProps }) => (
                <Stack
                  direction="row"
                  spacing="1rem"
                  sx={{
                    width: '100%',
                  }}
                >
                  <TextField
                    {...startProps}
                    size="medium"
                    variant="standard"
                    sx={{
                      width: '50%',
                    }}
                    label="From"
                    error={Boolean(errors?.from)}
                    helperText={errors?.from?.message}
                    placeholder="mm/dd/yyyy"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Calendar2Icon
                            sx={{
                              fontSize: '1rem',
                              color: ({ palette }) => palette.common.black,
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    {...endProps}
                    size="medium"
                    variant="standard"
                    sx={{
                      width: '50%',
                    }}
                    label="To"
                    error={Boolean(errors?.to)}
                    helperText={errors?.to?.message}
                    placeholder="mm/dd/yyyy"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Calendar2Icon
                            sx={{
                              fontSize: '1rem',
                              color: ({ palette }) => palette.common.black,
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>
              )}
            />

            <Controller
              control={control}
              name="system_posting"
              render={({ field }) => (
                <FormControlLabel
                  sx={{ m: 0 }}
                  {...field}
                  checked={field.value ?? false}
                  onChange={(e, checked) => field.onChange(checked)}
                  label={
                    <Typography variant="inter14" fontWeight={500} sx={{ ml: '1rem' }}>
                      System Posting
                    </Typography>
                  }
                  control={<Switch />}
                />
              )}
            />

            <Controller
              control={control}
              name="billing"
              render={({ field }) => (
                <FormControl
                  sx={{
                    boxShadow: 'inset 0 -1px 0 0 #d2d7de',
                    pb: 2,
                  }}
                >
                  <InputLabel
                    shrink
                    sx={{
                      mt: 2,
                    }}
                  >
                    Billing
                  </InputLabel>

                  <RadioGroup
                    value={field.value}
                    onChange={e => field.onChange(e.target.value)}
                    row
                    sx={{
                      mt: 2,
                    }}
                  >
                    {Object.values(TransactionListBillingNum).map(type => (
                      <FormControlLabel
                        key={type}
                        value={type}
                        control={<Radio />}
                        label={t(`repots.cash_register.transaction_list.billing.${type}` as any)}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}
            />

            <Controller
              control={control}
              name="cashier_ids"
              render={({ field }) => (
                <CashierField value={field.value || []} onChange={value => field.onChange(value)} />
              )}
            />

            <Controller
              control={control}
              name="type"
              render={({ field }) => (
                <FormControl
                  sx={{
                    boxShadow: 'inset 0 -1px 0 0 #d2d7de',
                    pb: 2,
                  }}
                >
                  <InputLabel
                    shrink
                    sx={{
                      mt: 2,
                    }}
                  >
                    type
                  </InputLabel>

                  <RadioGroup
                    value={field.value}
                    onChange={e => field.onChange(e.target.value)}
                    row
                    sx={{
                      mt: 2,
                    }}
                  >
                    {Object.values(TransactionListTypeNum).map(type => (
                      <FormControlLabel
                        key={type}
                        value={type}
                        control={<Radio />}
                        label={t(`repots.cash_register.transaction_list.list.${type}` as any)}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}
            />

            {/* <Controller
              control={control}
              name="type"
              render={({ field: { value, onChange } }) => {
                const currentValue: TransactionListTypeNum[] = Array.isArray(value) ? value : [];
                return (
                  <FormControl
                    sx={{
                      boxShadow: 'inset 0 -1px 0 0 #d2d7de',
                      pb: 2,
                    }}
                  >
                    <InputLabel
                      sx={{
                        mt: 2,
                      }}
                    >
                      Type
                    </InputLabel>
                    <FormGroup
                      sx={{
                        mt: 2,
                      }}
                    >
                      <FormControlLabel
                        sx={{ py: 0.5, pl: 1.5 }}
                        control={
                          <Checkbox
                            checked={
                              currentValue.length === Object.values(TransactionListTypeNum).length
                            }
                            onChange={() => {
                              onChange(
                                currentValue.length === Object.values(TransactionListTypeNum).length
                                  ? []
                                  : Object.values(TransactionListTypeNum)
                              );
                            }}
                            sx={{ mr: 1 }}
                          />
                        }
                        label="All"
                      />
                      {Object.values(TransactionListTypeNum).map((type: TransactionListTypeNum) => (
                        <FormControlLabel
                          key={type}
                          sx={{ py: 0.5, pl: 1.5 }}
                          control={
                            <Checkbox
                              checked={currentValue.includes(type)}
                              onChange={() => {
                                onChange(
                                  currentValue.includes(type)
                                    ? currentValue.filter(val => val !== type)
                                    : [...currentValue, type]
                                );
                              }}
                              sx={{ mr: 1 }}
                            />
                          }
                          label={t(`repots.cash_register.transaction_list.list.${type}` as any)}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                );
              }}
            /> */}

            {[TransactionListTypeNum.PAYMENT, TransactionListTypeNum.ALL].includes(
              formValues?.type
            ) && (
              <Controller
                control={control}
                name="cash_register_ids"
                render={({ field }) => (
                  <CashRegisterField
                    value={field.value || []}
                    onChange={value => field.onChange(value)}
                  />
                )}
              />
            )}

            {/* <Controller
              control={control}
              name="operation_id"
              render={({ field }) => (
                <OperationField value={field.value} onChange={value => field.onChange(value)} />
              )}
            /> */}

            {![TransactionListTypeNum.PAYMENT, TransactionListTypeNum.ALL].includes(
              formValues?.type
            ) && (
              <Controller
                control={control}
                name="charge_group_ids"
                render={({ field }) => (
                  <ChargeGroupField value={field.value} onChange={value => field.onChange(value)} />
                )}
              />
            )}
            {![TransactionListTypeNum.PAYMENT].includes(formValues?.type) && (
              <Controller
                control={control}
                name="billing_group_ids"
                render={({ field }) => (
                  <BillingGroupField
                    value={field.value}
                    onChange={value => field.onChange(value)}
                  />
                )}
              />
            )}
            <Controller
              control={control}
              name="charge_code_ids"
              render={({ field }) => (
                <ChargeCodeField
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  filterFunction={chargeCodeItem => {
                    if (formValues.type === TransactionListTypeNum.PAYMENT) {
                      return chargeCodeItem.charge_type === ChargeCodeTypesModel.PAYMENT;
                    } else if (formValues.type === TransactionListTypeNum.CHARGE) {
                      return chargeCodeItem.charge_type !== ChargeCodeTypesModel.PAYMENT;
                    } else {
                      return true;
                    }
                  }}
                />
              )}
            />

            <Controller
              control={control}
              name="order_by"
              render={({ field }) => (
                <FormControl
                  sx={{
                    pt: 2,
                  }}
                >
                  <InputLabel shrink sx={{ mt: 2 }}>
                    Order By
                  </InputLabel>

                  <RadioGroup
                    value={field.value}
                    onChange={e => field.onChange(e.target.value)}
                    row
                  >
                    {Object.values(TransactionListOrderByNum).map(type => (
                      <FormControlLabel
                        key={type}
                        value={type}
                        control={<Radio />}
                        label={t(`repots.cash_register.transaction_list.order_by.${type}` as any)}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}
            />
            <Box sx={{ height: 10 }} />
          </Stack>
        </Stack>

        <FooterExtraButtons
          onSubmit={file_type => {
            setValue('file_type', file_type);
            handleSubmit(onSubmit)();
          }}
          exportFileTypes={[
            {
              label: 'PDF',
              value: 'pdf',
              icon: <FilePdfIcon sx={{ width: '3rem', height: '3rem' }} />,
            },
            {
              label: 'EXCEL',
              value: 'excel',
              icon: <FileExcelIcon sx={{ width: '3rem', height: '3rem' }} />,
            },
            {
              label: 'CSV',
              value: 'csv',
              icon: <FileCsvIcon sx={{ width: '3rem', height: '3rem' }} />,
            },
          ]}
          onReset={onReset}
        />
      </Stack>
    </Box>
  );
};
